import { useCallback, useEffect, useState } from 'react';
import { TextInput, View } from 'react-native';

import { SearchPicker } from '@/components/search/SearchPicker.web';
import { Dialog, DialogButton, Text } from '@/components/ui';
import type { ContextScopeRoot, ContextSet } from '@/lib/chat/types';
import {
  isScopeRootSelected,
  loadScopeTree,
  toggleScopeRoot,
  withScopeRoots,
  type ScopeTreeNode,
} from '@/lib/chat/scope-tree';

type ChatDiscoveryScopeDialogProps = {
  open: boolean;
  contextSet: ContextSet;
  onClose: () => void;
  onContextSetChange: (next: ContextSet) => void;
};

function FolderGlyph({ open }: { open: boolean }) {
  return (
    <span className="chat-scope-row-icon" aria-hidden>
      {open ? '📂' : '📁'}
    </span>
  );
}

function DatabaseGlyph() {
  return (
    <span className="chat-scope-row-icon" aria-hidden>
      ▦
    </span>
  );
}

function ScopeRow({
  node,
  depth,
  roots,
  onToggle,
}: {
  node: ScopeTreeNode;
  depth: number;
  roots: ContextScopeRoot[];
  onToggle: (node: ScopeTreeNode) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children.length > 0;
  const selected = isScopeRootSelected(roots, node.id);

  return (
    <>
      <div className="chat-scope-row" style={{ paddingLeft: 8 + depth * 16 }}>
        <button
          type="button"
          className="chat-scope-row-twisty"
          disabled={!hasChildren}
          onClick={() => setExpanded((value) => !value)}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {hasChildren ? (expanded ? '▾' : '▸') : ''}
        </button>
        <label className="chat-scope-row-label">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(node)}
          />
          {node.type === 'database' ? <DatabaseGlyph /> : <FolderGlyph open={expanded} />}
          <span className="chat-scope-row-title">{node.title}</span>
        </label>
      </div>
      {expanded && hasChildren
        ? node.children.map((child) => (
            <ScopeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              roots={roots}
              onToggle={onToggle}
            />
          ))
        : null}
    </>
  );
}

export function ChatDiscoveryScopeDialog({
  open,
  contextSet,
  onClose,
  onContextSetChange,
}: ChatDiscoveryScopeDialogProps) {
  const [tree, setTree] = useState<ScopeTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const roots = contextSet.scopeRoots ?? [];

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadScopeTree()
      .then((nodes) => {
        if (!cancelled) {
          setTree(nodes);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load pages');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const onToggle = useCallback(
    (node: ScopeTreeNode) => {
      const nextRoots = toggleScopeRoot(roots, { id: node.id, title: node.title });
      onContextSetChange(withScopeRoots(contextSet, nextRoots));
    },
    [contextSet, onContextSetChange, roots],
  );

  const onClear = useCallback(() => {
    onContextSetChange(withScopeRoots(contextSet, []));
  }, [contextSet, onContextSetChange]);

  return (
    <Dialog
      open={open}
      title="Discovery scope"
      onClose={onClose}
      footer={
        <>
          <DialogButton label="Clear" onPress={onClear} disabled={roots.length === 0} />
          <DialogButton label="Done" variant="primary" onPress={onClose} />
        </>
      }
    >
      <Text variant="caption" className="mb-2 text-muted">
        Pick the areas the agent may search. It can read the selected pages and everything nested
        under them. Leave empty to search the whole workspace.
      </Text>

      <View className="mb-3">
        <TextInput
          className="mb-2 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-fg"
          placeholder="Search to add pages…"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery.trim() ? (
          <SearchPicker
            query={searchQuery}
            selectedIndex={0}
            showRecentsWhenEmpty={false}
            heading="Add to scope"
            onSelect={(item) => {
              const nextRoots = toggleScopeRoot(roots, {
                id: item.containerId,
                title: item.title,
              });
              onContextSetChange(withScopeRoots(contextSet, nextRoots));
              setSearchQuery('');
            }}
          />
        ) : null}
      </View>

      {loading ? (
        <Text variant="caption" className="text-muted">
          Loading pages…
        </Text>
      ) : null}

      {error ? (
        <Text variant="caption" className="text-danger">
          {error}
        </Text>
      ) : null}

      {!loading && !error && tree.length === 0 ? (
        <Text variant="caption" className="text-muted">
          No pages found.
        </Text>
      ) : null}

      {!loading && !error && tree.length > 0 ? (
        <div className="chat-scope-tree">
          {tree.map((node) => (
            <ScopeRow key={node.id} node={node} depth={0} roots={roots} onToggle={onToggle} />
          ))}
        </div>
      ) : null}
    </Dialog>
  );
}
